import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import feChangePaymentOption from 'actions/payment/feChangePaymentOption.action';
import IMG_PAY_CARD from 'assets/images/modals/card.png';
import IMG_PAY_CASH from 'assets/images/modals/cash.png';
import IMG_PAY_TERMINAL from 'assets/images/modals/terminal.png';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import CHECKOUT_OPTION from 'constants/checkoutOption.const';
import ELID from 'constants/elid.const';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import focusElement from 'utils/dom/focusElement.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isNotEnumVal from 'utils/predicates/enum/isNotEnumVal.util';

import CN from './SelectPaymentOptionModal.module.scss';

const CTAG = 'SelectPaymentOptionModal';
const L = logger(CTAG);

const MAP = Object.freeze({
  [CHECKOUT_OPTION.slick]: ELID.checkoutSlick,
  [CHECKOUT_OPTION.terminal]: ELID.checkoutTerminal,
  [CHECKOUT_OPTION.cash]: ELID.checkoutCash,
});

const SelectPaymentOptionModal = ({ client, feChangePaymentOption, onClosePaymentModal }) => {
  const [value, setValue] = useState('');
  const [isProceedDisabled, setIsProceedDisabled] = useState(true);
  const { t } = useTranslation();

  const userName = asObject(client).user_name;

  const firstName = fromAnyToString(userName).split(S.space).slice(0, 1).join(S.space);

  const handleSelect = useCallback((ev) => {
    const val = fromEventToValue(ev);

    setValue(val);
    setIsProceedDisabled(isNotEnumVal(CHECKOUT_OPTION, val));
  }, []);

  const handleProceed = useCallback(() => {
    feChangePaymentOption(value);

    const elementId = MAP[value];
    if (elementId) {
      // TODO: use the value of selectCheckoutPaymentOption() to perform focus in the appropriate component, not here
      // SEE: src/checkout/components/CheckoutFooterLine.jsx
      L.debug('handleProceed()', 'Focusing element by elementId:', elementId);
      focusElement(document.getElementById(elementId));
    }

    call(`${CTAG}.onClosePaymentModal`, onClosePaymentModal, value);
  }, [feChangePaymentOption, onClosePaymentModal, value]);

  return (
    <SalonModal tag="" banner="backgroundImage" isOpen>
      <SalonModal.Header>
        <h2 className={CN.headerTitle}>{t('checkout.selectPaymentOptionModal.title')}</h2>
      </SalonModal.Header>
      <SalonModal.Body>
        <div className={CN.modalContent}>
          <div className={CN.modalTitle}>{t('checkout.selectPaymentOptionModal.subtitle', { name: firstName })}</div>

          <div data-bem="SelectPaymentOptionModal__option-slick" className={CN.group}>
            <div data-bem="SelectPaymentOptionModal__option-slick">
              <label
                className={cn({
                  [CN.item]: true,
                  [CN.selected]: value === CHECKOUT_OPTION.slick,
                })}
                htmlFor="r1"
              >
                <div className={CN.inner}>
                  <input
                    id="r1"
                    value={CHECKOUT_OPTION.slick}
                    name="r1"
                    type="radio"
                    checked={value === CHECKOUT_OPTION.slick}
                    onChange={handleSelect}
                  />
                  <div className={CN.iconContainer}>
                    <Icon variant={value === CHECKOUT_OPTION.slick ? 'markCircle' : 'markCircleUnselected'} />
                  </div>
                  <div className={CN.imgContainer}>
                    <img src={IMG_PAY_CARD} className={CN.image} alt="" />
                  </div>
                </div>

                <div className={CN.itemTitle}>{t('checkout.selectPaymentOptionModal.option1')}</div>
              </label>
            </div>

            <div data-bem="SelectPaymentOptionModal__option-terminal">
              <label
                className={cn({
                  [CN.item]: true,
                  [CN.selected]: value === CHECKOUT_OPTION.terminal,
                })}
                htmlFor="r2"
              >
                <div className={CN.inner}>
                  <input
                    id="r2"
                    value={CHECKOUT_OPTION.terminal}
                    name="r2"
                    type="radio"
                    checked={value === CHECKOUT_OPTION.terminal}
                    onChange={handleSelect}
                  />
                  <div className={CN.iconContainer}>
                    <Icon variant={value === CHECKOUT_OPTION.terminal ? 'markCircle' : 'markCircleUnselected'} />
                  </div>
                  <div className={CN.imgContainer}>
                    <img src={IMG_PAY_TERMINAL} className={CN.image} alt="" />
                  </div>
                </div>
                <div className={CN.itemTitle}>{t('checkout.selectPaymentOptionModal.option2')}</div>
              </label>
            </div>

            <div data-bem="SelectPaymentOptionModal__option-cash">
              <label
                htmlFor="r3"
                className={cn({
                  [CN.item]: true,
                  [CN.selected]: value === CHECKOUT_OPTION.cash,
                })}
              >
                <div className={CN.inner}>
                  <input
                    id="r3"
                    value={CHECKOUT_OPTION.cash}
                    name="r3"
                    type="radio"
                    checked={value === CHECKOUT_OPTION.cash}
                    onChange={handleSelect}
                  />
                  <div className={CN.iconContainer}>
                    <Icon variant={value === CHECKOUT_OPTION.cash ? 'markCircle' : 'markCircleUnselected'} />
                  </div>
                  <div className={CN.imgContainer}>
                    <img src={IMG_PAY_CASH} className={CN.image} alt="" />
                  </div>
                </div>

                <div className={CN.itemTitle}>{t('checkout.selectPaymentOptionModal.option3')}</div>
              </label>
            </div>
          </div>
        </div>
      </SalonModal.Body>
      <SalonModal.Footer>
        <Button variant="primaryForce" onClick={handleProceed} disabled={isProceedDisabled}>
          <span className={CN.buttonSpace}>{t('checkout.selectPaymentOptionModal.checkoutButton')}</span>
        </Button>
      </SalonModal.Footer>
    </SalonModal>
  );
};

const mapDispatchToProps = (dispatch) => bindActionCreators({ feChangePaymentOption }, dispatch);

export default connect(null, mapDispatchToProps)(SelectPaymentOptionModal);
